/*
 * Collection facets — AJAX filtering via the Section Rendering API.
 *
 * The mockup filters instantly from React state (src/App.jsx ~3012). Shopify
 * filtering is URL-driven, so instead of reloading we re-request the same URL
 * with `?section_id=<id>` and swap the regions marked `data-facet-id`, then
 * pushState the filtered URL. State stays shareable — something the mockup
 * can't do — and the <noscript> submit path still works with JS off.
 */
(() => {
  // Collection page and search results share this whole mechanism; only the
  // root attribute differs, so each page can be identified in the DOM.
  const root = document.querySelector('[data-collection-root], [data-facets-root]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const COLLAPSE_KEY = 'yyy:collection-filters-collapsed';
  // Regions replaced on every fetch. Order matters only for readability.
  const SWAP_IDS = ['toolbar-count', 'mobile-trigger', 'sort-control', 'chips', 'grid-header-count', 'results', 'apply-bar'];
  // Subset of SWAP_IDS that carry role="status"/aria-live: swapped by content,
  // never by node, or the announcement is lost. See swapRegions.
  const LIVE_IDS = new Set(['toolbar-count']);

  let controller = null;
  let debounceTimer = null;

  /* ---------------- filter state -> URL ---------------- */

  // Facet values are multi-valued — several checkboxes share one param name, so
  // they must append. These four are single-valued and must overwrite, or a
  // second apply on /search yields `?q=milk&q=milk`.
  const SINGLE_VALUE_KEYS = ['q', 'type', 'options[prefix]', 'sort_by'];

  // Build the query from the filter form plus the sort control, which lives in
  // the toolbar form rather than the facet form.
  const buildSearchParams = () => {
    const params = new URLSearchParams();
    const form = root.querySelector('[data-facets-form]');
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (value === '') return;
        if (SINGLE_VALUE_KEYS.includes(key)) params.set(key, value);
        else params.append(key, value);
      });
    }
    const sort = root.querySelector('.collection-toolbar__sort select');
    if (sort && sort.value) params.set('sort_by', sort.value);

    // Search results are scoped by `q`; drop it and the shopper is thrown to an
    // empty /search. The facet form carries it, but there is no facet form when
    // the store has no filters configured and only the sort control renders —
    // so fall back to the params already in the URL. No-op on collection pages,
    // whose URLs carry none of these.
    const current = new URLSearchParams(window.location.search);
    ['q', 'type', 'options[prefix]'].forEach((key) => {
      if (!params.has(key) && current.has(key)) params.set(key, current.get(key));
    });
    return params;
  };

  /* ---------------- rendering ---------------- */

  const swapRegions = (doc) => {
    SWAP_IDS.forEach((id) => {
      const next = doc.querySelector(`[data-facet-id="${id}"]`);
      const current = root.querySelector(`[data-facet-id="${id}"]`);
      if (!next || !current) return;

      // A live region only announces mutations that happen *inside* a region
      // the AT is already tracking. replaceWith() destroys the tracked node and
      // inserts a fresh one, whose starting content counts as initial state and
      // is not spoken — so role="status" was here but the count never actually
      // reached a screen reader. Update these in place to keep the node alive.
      if (LIVE_IDS.has(id)) {
        if (current.innerHTML !== next.innerHTML) current.innerHTML = next.innerHTML;
        return;
      }
      current.replaceWith(next);
    });

    // The filter form is swapped separately so focus can be restored onto the
    // control the user just toggled — otherwise every tick drops focus to body.
    const nextForm = doc.querySelector('[data-facets-form]');
    const currentForm = root.querySelector('[data-facets-form]');
    if (nextForm && currentForm) {
      const active = document.activeElement;
      const key = active && active.name ? `${active.name}::${active.value}` : null;
      currentForm.replaceWith(nextForm);
      if (key) {
        const restored = nextForm.querySelector(
          `[name="${CSS.escape(key.split('::')[0])}"][value="${CSS.escape(key.split('::')[1])}"]`
        );
        if (restored) restored.focus();
      }
    }

    // The in-panel chip copy sits outside the form, so refresh it too.
    const nextPanelChips = doc.querySelector('.collection-chips--panel');
    const currentPanelChips = root.querySelector('.collection-chips--panel');
    if (nextPanelChips && currentPanelChips) currentPanelChips.replaceWith(nextPanelChips);
    else if (nextPanelChips && !currentPanelChips) {
      root.querySelector('.collection-filters__head')?.after(nextPanelChips);
    } else if (!nextPanelChips && currentPanelChips) currentPanelChips.remove();

    const nextClear = doc.querySelector('.collection-filters__clear');
    const currentClear = root.querySelector('.collection-filters__clear');
    if (nextClear && currentClear) currentClear.replaceWith(nextClear);
    else if (nextClear && !currentClear) {
      root.querySelector('.collection-filters__actions')?.prepend(nextClear);
    } else if (!nextClear && currentClear) currentClear.remove();
  };

  const render = async (url, { push = true, scroll = false } = {}) => {
    if (controller) controller.abort();
    controller = new AbortController();
    root.classList.add('is-loading');

    const separator = url.includes('?') ? '&' : '?';
    try {
      const response = await fetch(`${url}${separator}section_id=${sectionId}`, {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      swapRegions(html);
      if (push) window.history.pushState({ facets: true }, '', url);
      if (scroll) {
        root.querySelector('[data-collection-grid-start]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to a normal navigation rather than stranding the user.
      window.location.assign(url);
    } finally {
      root.classList.remove('is-loading');
    }
  };

  const applyFilters = () => {
    const params = buildSearchParams();
    const query = params.toString();
    render(query ? `${window.location.pathname}?${query}` : window.location.pathname);
  };

  /* ---------------- events ---------------- */

  root.addEventListener('change', (event) => {
    const field = event.target.closest('[data-facet-input]');
    if (!field) return;
    if (field.hasAttribute('data-facet-debounce')) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyFilters, 300);
    } else {
      applyFilters();
    }
  });

  // Price inputs also fire on keystroke so the debounce feels live.
  root.addEventListener('input', (event) => {
    if (!event.target.closest('[data-facet-debounce]')) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyFilters, 300);
  });

  // Chips, Clear all, and pagination are plain links — intercept and AJAX them.
  root.addEventListener('click', (event) => {
    const link = event.target.closest('.collection-chip, .collection-clear, .collection-filters__clear, [data-pagination] a');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || link.target === '_blank') return;
    event.preventDefault();
    render(link.getAttribute('href'), { scroll: link.closest('[data-pagination]') !== null });
  });

  window.addEventListener('popstate', () => {
    render(window.location.href, { push: false });
  });

  /* ---------------- show more / show less ---------------- */

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-filter-more]');
    if (!button) return;
    const group = button.closest('[data-filter-group]');
    const expanded = button.getAttribute('aria-expanded') === 'true';
    group.querySelectorAll('.collection-filter-row--overflow').forEach((row) => {
      row.hidden = expanded;
    });
    button.setAttribute('aria-expanded', String(!expanded));
    const text = button.querySelector('[data-filter-more-text]');
    if (text) text.textContent = expanded ? button.dataset.moreLabel : button.dataset.lessLabel;
  });

  /* ---------------- desktop sidebar collapse ---------------- */

  const layout = root.querySelector('[data-collection-layout]');
  const setCollapsed = (collapsed) => {
    if (!layout) return;
    layout.classList.toggle('collection-layout--collapsed', collapsed);
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch (error) {
      /* private mode — collapse just won't persist */
    }
  };

  try {
    if (localStorage.getItem(COLLAPSE_KEY) === '1') setCollapsed(true);
  } catch (error) {
    /* ignore */
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-filters-collapse]')) {
      setCollapsed(true);
      root.querySelector('[data-filters-expand]')?.focus();
    }
    if (event.target.closest('[data-filters-expand]')) {
      setCollapsed(false);
      root.querySelector('[data-filters-collapse]')?.focus();
    }
  });

  /* ---------------- mobile drawer ---------------- */

  const panel = root.querySelector('[data-filter-panel]');
  const overlay = root.querySelector('.collection-filters__overlay');

  if (panel) {
    const setOpen = (open) => {
      panel.classList.toggle('is-open', open);
      if (overlay) overlay.hidden = !open;
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        // The sheet is visibility:hidden until the class lands, and focus() is
        // ignored on a still-hidden element — so it has to wait for the frame
        // in which the panel is actually visible. Without this the sheet opens
        // with focus stranded on <body>.
        requestAnimationFrame(() => {
          (panel.querySelector('[data-filter-drawer-close]') || panel.querySelector('input, button'))?.focus();
        });
      } else {
        root.querySelector('[data-filter-drawer-open]')?.focus();
      }
    };

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-drawer-open]')) setOpen(true);
      if (event.target.closest('[data-filter-drawer-close]')) setOpen(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel.classList.contains('is-open')) setOpen(false);
    });
  }

  /* ---------------- within-collection search (mobile only) ---------------- */

  // Client-side and current-page-only, matching the mockup. It hides rendered
  // cards; it does not re-query the collection.
  const search = root.querySelector('[data-collection-search]');
  if (search) {
    search.addEventListener('input', () => {
      const term = search.value.trim().toLowerCase();
      const grid = root.querySelector('.collection-template__grid');
      if (!grid) return;

      let matches = 0;
      Array.from(grid.children).forEach((card) => {
        const hit = term === '' || card.textContent.toLowerCase().includes(term);
        card.hidden = !hit;
        if (hit) matches += 1;
      });

      // Hiding cards with no running count leaves the shopper staring at an
      // unexplained empty grid, so the result of every keystroke is stated.
      const status = root.querySelector('[data-collection-search-status]');
      if (!status) return;
      if (term === '') {
        status.textContent = '';
        status.hidden = true;
      } else {
        status.textContent = matches === 0
          ? `No products on this page match “${search.value.trim()}”. Clear the search to see the full collection.`
          : `${matches} product${matches === 1 ? '' : 's'} on this page match “${search.value.trim()}”.`;
        status.hidden = false;
      }
    });
  }
})();
